import React, { useMemo, useRef, useState } from "react";
import { config as baseConfig, pieConfig } from "../config";
import { resolveTokenColor, formatDataValue } from "../SupportFiles/chartUtils";
import { useUniqueComponentId } from "../SupportFiles/uniqueId";
import InfoData from "../SupportFiles/InfoData";
import NoData from "../../Info/NoData";

function toRad(deg) {
  return (deg * Math.PI) / 180;
}
function arcPath(cx, cy, rOuter, rInner, startAngle, endAngle) {
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  const sxOuter = cx + rOuter * Math.cos(startAngle);
  const syOuter = cy + rOuter * Math.sin(startAngle);
  const exOuter = cx + rOuter * Math.cos(endAngle);
  const eyOuter = cy + rOuter * Math.sin(endAngle);
  const sxInner = cx + rInner * Math.cos(endAngle);
  const syInner = cy + rInner * Math.sin(endAngle);
  const exInner = cx + rInner * Math.cos(startAngle);
  const eyInner = cy + rInner * Math.sin(startAngle);
  return [
    `M ${sxOuter} ${syOuter}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${exOuter} ${eyOuter}`,
    `L ${sxInner} ${syInner}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 0 ${exInner} ${eyInner}`,
    `Z`,
  ].join(" ");
}
function pointOnArc(cx, cy, r, angle) {
  return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
}

const PieChartNew = ({
  series = [{ name: "data", data: [] }],
  categories = [],
  title,
  height = 360,
  width = 600,
  colors,
  dataLabelsEnabled = baseConfig?.showLabels?.show || false,
  padding: paddingProp,
  colorMap,
  pieOptions,
  componentId,
  onHover,
}) => {
  const generatedId = useUniqueComponentId("charts-adv");
  const localId = componentId || generatedId;
  const basePad = (paddingProp ?? baseConfig.padding) || {
    top: 16,
    right: 12,
    bottom: 28,
    left: 40,
  };
  const svgW = typeof width === "number" ? width : Number(width) || 600;
  const svgH = typeof height === "number" ? height : Number(height) || 360;
  const infoCfg = {
    ...(pieConfig?.pie?.infoSection || {}),
    ...((pieOptions && pieOptions.infoSection) || {}),
  };
  const infoShow = !!infoCfg.show;
  const infoSide = infoCfg.side || "right";
  const infoWidth =
    typeof infoCfg.width === "number" ? Math.max(80, infoCfg.width) : 200;
  const chartW = infoShow ? Math.floor(svgW / 2) : svgW;
  const p = {
    top: basePad.top,
    right: basePad.right,
    bottom: basePad.bottom,
    left: basePad.left,
  };
  const axisLabelFontSizePx =
    typeof baseConfig.axis?.fontSize === "number"
      ? baseConfig.axis.fontSize
      : 12;
  const axisLabelColor = resolveTokenColor(baseConfig.axis.labelColor);
  const gridBg = resolveTokenColor(baseConfig.grid?.background);
  const resolvedColors = useMemo(
    () =>
      (colors || baseConfig.colors).map((c, i) => {
        const resolved =
          (colorMap && colorMap[i]) || resolveTokenColor(c) || "#3b82f6";
        return resolved;
      }),
    [colors, colorMap],
  );
  const pieBorderWidth =
    typeof pieOptions?.borderWidth === "number"
      ? Math.max(0, pieOptions.borderWidth)
      : typeof pieConfig?.pie?.borderWidth === "number"
        ? Math.max(0, pieConfig.pie.borderWidth)
        : 0;
  const pieBorderColorToken =
    (pieOptions && pieOptions.borderColor) ||
    (pieConfig?.pie && pieConfig.pie.borderColor) ||
    "transparent";
  const pieBorderColor = resolveTokenColor(pieBorderColorToken);
  const pieGapWidth =
    typeof pieOptions?.gap === "number"
      ? Math.max(0, pieOptions.gap)
      : typeof pieConfig?.pie?.gap === "number"
        ? Math.max(0, pieConfig.pie.gap)
        : 0;
  const bgResolved = resolveTokenColor(baseConfig.background);
  const gapColor =
    gridBg && gridBg !== "transparent"
      ? gridBg
      : bgResolved && bgResolved !== "transparent"
        ? bgResolved
        : "#fff";
  const valueSeries = useMemo(() => {
    const first = series.find((s) => !s.__projection) ||
      series[0] || {
        name: "data",
        data: [],
      };
    return first;
  }, [series]);
  const values = valueSeries.data.map((v) => {
    const n = v != null && isFinite(v) ? Number(v) : 0;
    return n > 0 ? n : 0;
  });
  const total = values.reduce((acc, v) => acc + v, 0);
  const renderEntries = useMemo(
    () =>
      values.map((value, idx) => ({ idx, value })).filter((it) => it.value > 0),
    [values],
  );
  const type =
    (pieOptions && pieOptions.type) ||
    (pieConfig?.pie && pieConfig.pie.type) ||
    "donut";
  const innerRatio =
    typeof pieOptions?.innerRatio === "number"
      ? Math.max(0, Math.min(0.95, pieOptions.innerRatio))
      : typeof pieConfig?.pie?.innerRatio === "number"
        ? Math.max(0, Math.min(0.95, pieConfig.pie.innerRatio))
        : type === "donut"
          ? 0.6
          : 0;
  const padAngleDeg =
    typeof pieOptions?.padAngleDeg === "number"
      ? Math.max(0, Math.min(10, pieOptions.padAngleDeg))
      : typeof pieConfig?.pie?.padAngleDeg === "number"
        ? Math.max(0, Math.min(10, pieConfig.pie.padAngleDeg))
        : 0;
  const cx = Math.round((chartW - p.left - p.right) / 2) + p.left;
  const cy = Math.round((svgH - p.top - p.bottom) / 2) + p.top;
  const radiusOuter = Math.max(
    10,
    Math.floor(
      Math.min(chartW - p.left - p.right, svgH - p.top - p.bottom) / 2,
    ),
  );
  const radiusInner = Math.max(0, Math.floor(radiusOuter * innerRatio));
  const labelFontSize =
    typeof baseConfig.showLabels?.fontSize === "number"
      ? `${baseConfig.showLabels.fontSize}px`
      : undefined;
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);
  const handleMove = (e) => {
    const r = svgRef.current?.getBoundingClientRect();
    if (!r || total <= 0 || renderEntries.length === 0) {
      setHover(null);
      if (onHover) onHover(null);
      return;
    }
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const dx = x - cx;
    const dy = y - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < radiusInner || dist > radiusOuter) {
      setHover(null);
      if (onHover) onHover(null);
      return;
    }
    if (renderEntries.length === 1) {
      const idx = renderEntries[0].idx;
      setHover({ i: idx });
      if (onHover) {
        const tipX = e.clientX;
        const tipY = e.clientY;
        const rows = [
          {
            name:
              categories[idx] ||
              (valueSeries.name
                ? `${valueSeries.name} ${idx + 1}`
                : `Slice ${idx + 1}`),
            color:
              (colorMap && colorMap[idx]) ||
              resolvedColors[idx % resolvedColors.length] ||
              "#3b82f6",
            value: formatDataValue(values[idx]),
          },
        ];
        onHover({
          index: idx,
          guidelineX: tipX,
          topY: tipY,
          category: categories[idx],
          values: [values[idx]],
          componentId: localId,
          rows,
        });
      }
      return;
    }
    const angle = Math.atan2(dy, dx);
    let a = angle;
    if (a < -Math.PI / 2) a += 2 * Math.PI;
    const startBase = -Math.PI / 2;
    let cum = startBase;
    let idx = -1;
    for (let j = 0; j < renderEntries.length; j++) {
      const entry = renderEntries[j];
      const frac = total > 0 ? entry.value / total : 0;
      const span = frac * 2 * Math.PI - toRad(padAngleDeg);
      const end = cum + Math.max(0, span);
      if (a >= cum && a < end) {
        idx = entry.idx;
        break;
      }
      cum = end + toRad(padAngleDeg);
    }
    if (idx < 0) {
      setHover(null);
      if (onHover) onHover(null);
      return;
    }
    setHover({ i: idx });
    if (onHover) {
      const tipX = e.clientX;
      const tipY = e.clientY;
      const rows = [
        {
          name:
            categories[idx] ||
            (valueSeries.name
              ? `${valueSeries.name} ${idx + 1}`
              : `Slice ${idx + 1}`),
          color:
            (colorMap && colorMap[idx]) ||
            resolvedColors[idx % resolvedColors.length] ||
            "#3b82f6",
          value: formatDataValue(values[idx]),
        },
      ];
      onHover({
        index: idx,
        guidelineX: tipX,
        topY: tipY,
        category: categories[idx],
        values: [values[idx]],
        componentId: localId,
        rows,
      });
    }
  };
  const handleLeave = () => {
    setHover(null);
    if (onHover) onHover(null);
  };
  const slices = useMemo(() => {
    let start = -Math.PI / 2;
    return renderEntries.map((entry) => {
      const frac = total > 0 ? entry.value / total : 0;
      const span = Math.max(0, frac * 2 * Math.PI - toRad(padAngleDeg));
      const end = start + span;
      const d = arcPath(cx, cy, radiusOuter, radiusInner, start, end);
      const mid = start + span / 2;
      const i = entry.idx;
      const color =
        (colorMap && colorMap[i]) ||
        resolvedColors[i % resolvedColors.length] ||
        "#3b82f6";
      const labelPos = pointOnArc(cx, cy, radiusOuter + 12, mid);
      const out = {
        d,
        start,
        end,
        color,
        labelPos,
        value: entry.value,
        idx: i,
      };
      start = end + toRad(padAngleDeg);
      return out;
    });
  }, [
    renderEntries,
    total,
    padAngleDeg,
    cx,
    cy,
    radiusOuter,
    radiusInner,
    colorMap,
    resolvedColors,
  ]);
  const noDataCfg = baseConfig?.noDataConfig || {};
  const showNoData =
    typeof noDataCfg.show === "boolean" ? noDataCfg.show : true;
  const isEmpty = total <= 0 || renderEntries.length === 0;
  if (isEmpty) {
    return (
      <div
        className="chartWrapper w-full h-full flex"
        style={{
          width: typeof width === "number" ? `${width}px` : width,
          height: typeof height === "number" ? `${height}px` : height,
        }}
      >
        {showNoData ? (
          <NoData
            title={noDataCfg.title}
            desc={noDataCfg.desc}
            color={noDataCfg.color}
            noImage={noDataCfg.noImage}
          />
        ) : (
          <div className="w-full h-full relative p-6">
            <svg
              className="w-full h-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="xMidYMid meet"
            >
              <rect x="0" y="0" width="100" height="100" fill="#ffffff" />
              <circle cx="50" cy="50" r="34" fill="#f3f4f6" />
              <circle cx="50" cy="50" r="20" fill="#ffffff" />
              <circle
                cx="50"
                cy="50"
                r="27"
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="14"
                strokeDasharray="24 8 18 8 14 8"
                transform="rotate(-90 50 50)"
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute bottom-4 left-0 right-0 text-center text-sm text-gray-500">
              No data available
            </div>
          </div>
        )}
      </div>
    );
  }
  const gridCols =
    infoShow && infoSide === "left"
      ? `${infoWidth}px ${chartW}px`
      : infoShow
        ? `${chartW}px ${infoWidth}px`
        : `${chartW}px`;
  return (
    <div
      className={`chartWrapper w-full h-full grid ${
        infoShow ? "grid-cols-2" : "grid-cols-1"
      }`}
      style={{ gridTemplateColumns: gridCols }}
    >
      <div
        className="chartContainer w-full h-full flex justify-end items-end"
        style={{ minWidth: 0 }}
      >
        <svg
          ref={svgRef}
          width={chartW}
          height={svgH}
          onMouseMove={handleMove}
          onMouseLeave={handleLeave}
        >
          <rect
            x={p.left}
            y={p.top}
            width={chartW - p.left - p.right}
            height={svgH - p.top - p.bottom}
            fill={gridBg}
          />
          <g>
            {slices.length === 1
              ? (() => {
                  const s = slices[0];
                  const ringR = Math.round((radiusOuter + radiusInner) / 2);
                  const ringW = Math.max(1, radiusOuter - radiusInner);
                  const isHover = hover && hover.i === s.idx;
                  const stroke = resolveTokenColor(s.color);
                  const outline = isHover ? "#fff" : "none";
                  const outlineW = isHover
                    ? Math.min(2, Math.round(ringW * 0.08))
                    : 0;
                  return (
                    <>
                      <circle
                        cx={cx}
                        cy={cy}
                        r={ringR}
                        fill="none"
                        stroke={stroke}
                        strokeWidth={ringW}
                      />
                      {pieBorderWidth > 0 &&
                        pieBorderColor !== "transparent" && (
                          <>
                            <circle
                              cx={cx}
                              cy={cy}
                              r={radiusOuter}
                              fill="none"
                              stroke={pieBorderColor}
                              strokeWidth={pieBorderWidth}
                            />
                            <circle
                              cx={cx}
                              cy={cy}
                              r={radiusInner}
                              fill="none"
                              stroke={pieBorderColor}
                              strokeWidth={pieBorderWidth}
                            />
                          </>
                        )}
                      {outlineW > 0 && (
                        <circle
                          cx={cx}
                          cy={cy}
                          r={ringR}
                          fill="none"
                          stroke={outline}
                          strokeWidth={outlineW}
                        />
                      )}
                    </>
                  );
                })()
              : slices.map((s) => {
                  const isHover = hover && hover.i === s.idx;
                  const baseStroke =
                    pieBorderWidth > 0 && pieBorderColor !== "transparent"
                      ? pieBorderColor
                      : "none";
                  const baseStrokeWidth =
                    pieBorderWidth > 0 ? pieBorderWidth : 0;
                  return (
                    <g key={`slice-${s.idx}`}>
                      <path d={s.d} fill={s.color} stroke="none" />
                      {pieGapWidth > 0 && (
                        <path
                          d={s.d}
                          fill="none"
                          stroke={gapColor}
                          strokeWidth={pieGapWidth}
                          strokeLinecap="round"
                        />
                      )}
                      {baseStrokeWidth > 0 && baseStroke !== "none" && (
                        <path
                          d={s.d}
                          fill="none"
                          stroke={baseStroke}
                          strokeWidth={baseStrokeWidth}
                        />
                      )}
                      {isHover && (
                        <path
                          d={s.d}
                          fill="none"
                          stroke="#fff"
                          strokeWidth={Math.max(1.5, baseStrokeWidth)}
                        />
                      )}
                    </g>
                  );
                })}
          </g>
          {title && (
            <text
              x={p.left}
              y={p.top - 6}
              fill={axisLabelColor}
              fontSize={`${axisLabelFontSizePx}px`}
            >
              {title}
            </text>
          )}
          {dataLabelsEnabled &&
            slices.map((s) => {
              const val = s.value;
              const tv = formatDataValue(val);
              return (
                <text
                  key={`lbl-${s.idx}`}
                  x={s.labelPos.x}
                  y={s.labelPos.y}
                  fill={s.color}
                  fontSize={labelFontSize}
                  textAnchor="middle"
                >
                  {tv}
                </text>
              );
            })}
          {type === "donut" && (
            <>
              <text
                x={cx}
                y={cy}
                fill={axisLabelColor}
                fontSize={`${Math.max(
                  10,
                  Math.round(axisLabelFontSizePx * 1.2),
                )}px`}
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {formatDataValue(total)}
              </text>
            </>
          )}
        </svg>
      </div>
      {infoShow && (
        <div className="infoSection flex items-center">
          {/* {categories.map((label, i) => {
            const color =
              (colorMap && colorMap[i]) ||
              resolvedColors[i % resolvedColors.length] ||
              "#3b82f6";
            const val = values[i] || 0;
            return (
              <div
                key={`info-${i}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "16px 1fr auto",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "9999px",
                    background: color,
                    display: "inline-block",
                  }}
                />
                <span
                  style={{
                    fontSize: "12px",
                    color: resolveTokenColor(baseConfig.axis.labelColor),
                  }}
                >
                  {label || `Slice ${i + 1}`}
                </span>
                <span
                  style={{
                    fontSize: "12px",
                    color: resolveTokenColor(baseConfig.axis.labelColor),
                  }}
                >
                  {formatDataValue(val)}
                </span>
              </div>
            );
          })} */}
          <InfoData
            labels={categories}
            values={values}
            colorMap={colorMap}
            colors={colors}
          />
        </div>
      )}
    </div>
  );
};

export default PieChartNew;
